import { memo } from 'react'
import { createTheme, ThemeProvider, Box, GlobalStyles } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs from 'dayjs'

const PICKER_POPPER_Z_INDEX = 9999

function DateCustom({
	name,
	setDataFunction,
	data,
	value,
	required,
	disabled,
	data_cy,
	className,
	label,
	isRange = false,
	startName = 'startDate',
	endName = 'endDate',
	startLabel = 'Start Date',
	endLabel = 'End Date',
	slotProps: incomingSlotProps,
	...props
}) {
	const handleChange = (newValue) => {
		if (typeof setDataFunction !== 'function' || !name) return
		setDataFunction((prev) => {
			const base =
				prev && typeof prev === 'object' && !Array.isArray(prev)
					? prev
					: data && typeof data === 'object'
						? data
						: {}
			return {
				...base,
				[name]: newValue,
			}
		})
	}

	const handleStartDateChange = (newValue) => {
		if (setDataFunction && data) {
			setDataFunction({
				...data,
				[startName]: newValue,
			})
		}
	}

	const handleEndDateChange = (newValue) => {
		if (setDataFunction && data) {
			setDataFunction({
				...data,
				[endName]: newValue,
			})
		}
	}

	const customTheme = createTheme({
		zIndex: {
			modal: PICKER_POPPER_Z_INDEX,
		},
		components: {
			MuiPickersCalendarHeader: {
				styleOverrides: {
					root: {
						backgroundColor: 'none',
						color: 'var(--Primary-Text-Color, #313131)',
						borderBottom: '1px solid var(--Stroke-Color, #E8EBEE)',
						'& button': {
							svg: {
								color: 'var(--Primary-Text-Color, #313131)',
							},
						},
					},
					label: {
						fontSize: 'var(--SubTitle-2, 14px)',
						fontWeight: 'var(--Medium, 500)',
					},
				},
			},
			MuiPickersFadeTransitionGroup: {
				styleOverrides: {
					root: {
						'& .MuiDayCalendar-header': {
							span: {
								color: 'var(--Primary-Text-Color, #313131)',
							},
						},
					},
				},
			},
			MuiPickersDay: {
				styleOverrides: {
					root: {
						color: 'var(--Primary-Text-Color, #313131)',
						fontWeight: 'var(--Bold, 600)',
						'&:hover': {
							backgroundColor: '#7CA6BD',
							color: '#fff',
						},
						'&.Mui-selected': {
							backgroundColor: '#7CA6BD',
							color: '#fff',
							'&:hover': {
								backgroundColor: '#7CA6BD',
								color: '#fff',
							},
							'&:focus': {
								backgroundColor: '#7CA6BD',
								color: '#fff',
							},
						},
						'&.MuiPickersDay-today': {
							border: '1px solid var(--Card-Border, #E8EBEE)',
							backgroundColor: 'var(--Card-Border, #E8EBEE)',
							color: 'var(--Primary-Text-Color, #313131)',
						},
					},
				},
			},
			MuiDateCalendar: {
				styleOverrides: {
					root: {
						borderRadius: '16px',
						border: '1px solid var(--Stroke-Color, #E8EBEE)',
						backgroundColor: 'var(--Card-Color, #fff)',
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						'&.MuiPickersPopper-paper': {
							backgroundColor: '#fff',
							borderRadius: '8px',
						},
					},
				},
			},
			MuiPickerPopper: {
				styleOverrides: {
					root: {
						backgroundColor: 'transparent',
						boxShadow: 'none',
						backdropFilter: 'none',
						zIndex: PICKER_POPPER_Z_INDEX,
					},
				},
			},
		},
	})

	const textFieldStyles = {
		width: '100%',
		'& .MuiPickersInputBase-root, & .MuiInputBase-root': {
			padding: '4px',
			borderRadius: '8px',
			border: '1px solid #E8EBEE',
			fontSize: '14px',
			color: '#313131',
			background: '#fff',
		},
		'& .MuiPickersSectionList-root': {
			color: '#313131',
			borderRadius: '8px',
			padding: '5px 0 5px 8px',
		},
		'& .MuiInputAdornment-root': {
			padding: '0',
			color: '#313131',
			maxHeight: 'unset',
			width: '30px',
			flex: '0 0 auto',
		},
		'& .MuiInputAdornment-root button': {
			padding: '0',
			color: '#313131',
			'& svg': {
				width: '20px',
				height: '20px',
			},
		},
		'& fieldset': {
			display: 'none',
		},
	}

	const pickerSlotProps = (textField) => ({
		...incomingSlotProps,
		textField: {
			placeholder: 'Select date',
			sx: textFieldStyles,
			...incomingSlotProps?.textField,
			...textField,
		},
		popper: {
			...incomingSlotProps?.popper,
			sx: {
				zIndex: `${PICKER_POPPER_Z_INDEX} !important`,
				...incomingSlotProps?.popper?.sx,
			},
		},
	})

	const labelStyle = {
		font: 'var(--body-font-three)',
		marginBottom: '5px',
		fontWeight: '500',
		fontSize: '14px',
		color: 'var(--secondary-Text)',
		display: 'block',
		marginBottom: '4px',
	}

	return (
		<ThemeProvider theme={customTheme}>
			<GlobalStyles
				styles={{
					'.MuiPickerPopper-root': {
						zIndex: `${PICKER_POPPER_Z_INDEX} !important`,
					},
				}}
			/>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				{isRange ? (
					<Box
						sx={{
							display: 'flex',
							gap: 2,
							flexDirection: 'column',
						}}
					>
						{/* Start Date Picker */}
						<Box sx={{ flex: 1 }}>
							<label className={className} style={labelStyle}>
								{startLabel}
								{required && (
									<span style={{ color: 'red' }}> *</span>
								)}
							</label>
							<DatePicker
								slots={{
									openPickerIcon: () => (
										<img
											src={'/images/calendar.svg'}
											alt="calendar icon"
											style={{ width: 20, height: 20 }}
										/>
									),
								}}
								value={
									data?.[startName]
										? dayjs(data[startName])
										: null
								}
								onChange={handleStartDateChange}
								disabled={disabled}
								maxDate={
									data?.[endName]
										? dayjs(data[endName])
										: undefined
								}
								{...props}
								slotProps={pickerSlotProps({
									placeholder: 'Select start date',
									'data-cy': data_cy
										? `${data_cy}-start`
										: undefined,
								})}
							/>
						</Box>

						{/* End Date Picker */}
						<Box sx={{ flex: 1 }}>
							<label className={className} style={labelStyle}>
								{endLabel}
								{required && (
									<span style={{ color: 'red' }}> *</span>
								)}
							</label>
							<DatePicker
								slots={{
									openPickerIcon: () => (
										<img
											src={'/images/calendar.svg'}
											alt="calendar icon"
											style={{ width: 20, height: 20 }}
										/>
									),
								}}
								value={
									data?.[endName]
										? dayjs(data[endName])
										: null
								}
								onChange={handleEndDateChange}
								disabled={disabled}
								minDate={
									new Date(data?.[startName]) > new Date()
										? dayjs(data[startName])
										: dayjs(new Date().getTime())
								}
								{...props}
								slotProps={pickerSlotProps({
									placeholder: 'Select end date',
									'data-cy': data_cy
										? `${data_cy}-end`
										: undefined,
								})}
							/>
						</Box>
					</Box>
				) : (
					<>
						{label && (
							<label className={className} style={labelStyle}>
								{label}
								{required && (
									<span style={{ color: 'red' }}> *</span>
								)}
							</label>
						)}
						<DatePicker
							slots={{
								openPickerIcon: () => (
									<img
										src={'/images/calendar.svg'}
										alt="calendar icon"
										style={{ width: 20, height: 20 }}
									/>
								),
							}}
							value={
								data && name && data[name] 
									? dayjs(data[name]) 
									: value 
									? dayjs(value) 
									: null
							}
							onChange={handleChange}
							disabled={disabled}
							{...props}
							slotProps={pickerSlotProps({
								'data-cy': data_cy,
							})}
						/>
					</>
				)}
			</LocalizationProvider>
		</ThemeProvider>
	)
}

export default memo(DateCustom)
